/**
 * Deployment Settings Validator
 *
 * Const schema map is the single source of truth for:
 *   1. Runtime validation (Zod schemas)
 *   2. Static types (inferred via DeploymentSettingsMap)
 *
 * Add a schema to the map → types flow everywhere automatically.
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { ApiDeploymentSettingsSchema } from "./Api.schema";
import { EmbedDeploymentSettingsSchema } from "./Embed/__json.schema";
import { GoogleSheetDeploymentSettingsSchema } from "./GoogleSheet.schema";
import {
  ExcelDeploymentSettingsSchema,
  OutlookAddInDeploymentSettingsSchema,
  PowerPointDeploymentSettingsSchema,
  WordDeploymentSettingsSchema
} from "./OfficeAddin/__json.schema";
import { requireGptLiveDelegationModel } from "./require-gpt-live-delegation-model";
import { requireKnownGeminiLiveModel } from "./require-known-gemini-live-model";
import { SlackDeploymentSettingsSchema } from "./Slack.schema";
import { TeamsDeploymentSettingsSchema } from "./Teams/__json.schema";
import { TelegramDeploymentSettingsSchema } from "./Telegram.schema";
import {
  PhoneCallDeploymentSettingsSchema,
  TwilioDeploymentSettingsSchema
} from "./Twilio/__json.schema";
import { WhatsAppDeploymentSettingsSchema } from "./WhatsApp/__json.schema";

/**
 * Passthrough schema for deployment types without full Zod schemas yet.
 * As schemas are added for each type, they replace PassthroughSettingsSchema
 * in the map below.
 */
export const PassthroughSettingsSchema = z.record(z.string(), z.unknown());

// ============================================================
// SCHEMA MAP (Single Source of Truth)
// ============================================================

/**
 * Maps every DeploymentType to its Zod schema.
 *
 * `as const satisfies` does two things:
 *   - `as const` preserves the specific schema types so z.infer works
 *   - `satisfies Record<...>` enforces exhaustiveness — adding a new
 *     DeploymentType to Prisma without adding it here is a compile error
 */
const DEPLOYMENT_SETTINGS_SCHEMAS = {
  // Fully validated
  [DbEnum.DeploymentType.EMBED]: EmbedDeploymentSettingsSchema,
  [DbEnum.DeploymentType.SLACK]: SlackDeploymentSettingsSchema,
  [DbEnum.DeploymentType.TEAMS]: TeamsDeploymentSettingsSchema,
  [DbEnum.DeploymentType.TELEGRAM]: TelegramDeploymentSettingsSchema,
  [DbEnum.DeploymentType.GOOGLE_SHEETS]: GoogleSheetDeploymentSettingsSchema,
  [DbEnum.DeploymentType.WHATSAPP]: WhatsAppDeploymentSettingsSchema,
  [DbEnum.DeploymentType.TWILIO_SMS]: TwilioDeploymentSettingsSchema,
  [DbEnum.DeploymentType.TWILIO_VOICE]: PhoneCallDeploymentSettingsSchema,
  [DbEnum.DeploymentType.API]: ApiDeploymentSettingsSchema,

  // Office Add-ins (with rate limiting support)
  [DbEnum.DeploymentType.OUTLOOK_ADDIN]: OutlookAddInDeploymentSettingsSchema,
  [DbEnum.DeploymentType.EXCEL_ADDIN]: ExcelDeploymentSettingsSchema,
  [DbEnum.DeploymentType.WORD_ADDIN]: WordDeploymentSettingsSchema,
  [DbEnum.DeploymentType.POWERPOINT_ADDIN]: PowerPointDeploymentSettingsSchema,

  // Passthrough (no full schema yet)
  [DbEnum.DeploymentType.GMAIL]: PassthroughSettingsSchema,
  [DbEnum.DeploymentType.OUTLOOK]: PassthroughSettingsSchema,
  [DbEnum.DeploymentType.IMAP]: PassthroughSettingsSchema,
  [DbEnum.DeploymentType.SMTP]: PassthroughSettingsSchema,
  [DbEnum.DeploymentType.FB_MESSENGER]: PassthroughSettingsSchema,
  [DbEnum.DeploymentType.INSTAGRAM]: PassthroughSettingsSchema,
  [DbEnum.DeploymentType.AIRTABLE]: PassthroughSettingsSchema,
  [DbEnum.DeploymentType.GOOGLE_MEET]: PassthroughSettingsSchema,
  [DbEnum.DeploymentType.ZOOM]: PassthroughSettingsSchema
} as const satisfies Record<DbEnum.DeploymentType, z.ZodTypeAny>;

// ============================================================
// TYPE MAP (Auto-derived from schemas)
// ============================================================

/**
 * Maps DeploymentType → inferred TypeScript type of its settings.
 *
 * Derived automatically from DEPLOYMENT_SETTINGS_SCHEMAS — never goes stale.
 *
 * @example
 * type EmbedSettings = DeploymentSettingsMap["EMBED"];
 * // = { primaryColor: string; position: "left" | "right"; ... }
 */
export type DeploymentSettingsMap = {
  [K in keyof typeof DEPLOYMENT_SETTINGS_SCHEMAS]: z.infer<(typeof DEPLOYMENT_SETTINGS_SCHEMAS)[K]>;
};

/** Union of all possible deployment settings types */
export type AnyDeploymentSettings = DeploymentSettingsMap[DbEnum.DeploymentType];

// ============================================================
// VALIDATION RESULT TYPE
// ============================================================

/**
 * Either spelling of a deployment type, as one type.
 *
 * `DbEnum.DeploymentType` is a nominal TypeScript `enum`; Prisma's own
 * `$Enums.DeploymentType` is a string-literal union over the identical members.
 * A literal is NOT assignable to an enum, so every backend caller — which holds
 * the Prisma spelling, because that is what a `Deployment` row carries — had to
 * cast to reach the functions below. One did, as `type as never`, and a cast
 * that exists only to cross a boundary is the boundary being wrong.
 *
 * The template literal DERIVES the union from the enum, so the two cannot drift
 * apart the way a hand-written copy would. `prisma-enum-parity.test.ts` is what
 * keeps the enum itself honest against Prisma.
 *
 * It is taken as `keyof` the schema map rather than written out: that is the
 * set these functions can actually index, so the constraint and the lookup are
 * the same fact. The map's computed enum keys widen to plain string literals,
 * which is why the Prisma spelling satisfies it and an enum member does too.
 */
export type DeploymentTypeKey = keyof typeof DEPLOYMENT_SETTINGS_SCHEMAS;

export type DeploymentSettingsValidationResult<T extends DeploymentTypeKey = DeploymentTypeKey> =
  | { success: true; data: DeploymentSettingsMap[T] }
  | { success: false; error: z.ZodError };

// ============================================================
// TYPE-SAFE ACCESSORS
// ============================================================

/**
 * Get the Zod schema for a deployment type's settings.
 *
 * Generic: when called with a literal type, the return type is the
 * specific schema (not ZodTypeAny), enabling callers to chain
 * `.parse()` / `.safeParse()` with full type inference.
 */
export function getDeploymentSettingsSchema<T extends DeploymentTypeKey>(
  type: T
): (typeof DEPLOYMENT_SETTINGS_SCHEMAS)[T] {
  return DEPLOYMENT_SETTINGS_SCHEMAS[type];
}

/**
 * Validate deployment settings for a given deployment type.
 *
 * Returns a discriminated result with the correct settings type.
 *
 * @example
 * const result = validateDeploymentSettings(DbEnum.DeploymentType.EMBED, body.settings);
 * if (result.success) {
 *   result.data.primaryColor; // ← typed as string
 * }
 */
export function validateDeploymentSettings<T extends DeploymentTypeKey>(
  type: T,
  settings: unknown
): DeploymentSettingsValidationResult<T> {
  const schema = DEPLOYMENT_SETTINGS_SCHEMAS[type];
  const result = schema.safeParse(settings);

  if (result.success) {
    return { success: true, data: result.data as DeploymentSettingsMap[T] };
  }

  return { success: false, error: result.error };
}

/**
 * Validate deployment settings ON THE WAY IN — create, update, or patch.
 *
 * Identical to {@link validateDeploymentSettings} plus the cross-field rules a
 * stored row is allowed to be read without, but must never be WRITTEN without:
 * a GPT-Live deployment names its delegation model
 * (`requireGptLiveDelegationModel`), and a Gemini deployment names a model the
 * registry carries (`requireKnownGeminiLiveModel`). Each carries why it is
 * write-only.
 *
 * The schema is the READ one with the rule attached, taken from the same map,
 * so a deployment type added there is covered here with no second edit. A
 * refinement cannot change what a schema OUTPUTS — it only adds issues — so
 * `data` is the value the read path returns and is asserted the same way.
 *
 * The refined schema is built per call rather than cached in a parallel map:
 * `superRefine` returns a clone, this runs once per deployment create, update
 * or patch, and a second map keyed on the same types is a second thing to keep
 * in step with the first.
 */
export function validateDeploymentSettingsForWrite<T extends DeploymentTypeKey>(
  type: T,
  settings: unknown
): DeploymentSettingsValidationResult<T> {
  const schema: z.ZodTypeAny = DEPLOYMENT_SETTINGS_SCHEMAS[type];
  const result = schema
    .superRefine(requireGptLiveDelegationModel)
    .superRefine(requireKnownGeminiLiveModel)
    .safeParse(settings);

  if (result.success) {
    return { success: true, data: result.data as DeploymentSettingsMap[T] };
  }

  return { success: false, error: result.error };
}

/**
 * Parse deployment settings (throws on validation failure).
 *
 * @throws ZodError if validation fails
 */
export function parseDeploymentSettings<T extends DeploymentTypeKey>(
  type: T,
  settings: unknown
): DeploymentSettingsMap[T] {
  const schema = DEPLOYMENT_SETTINGS_SCHEMAS[type];
  return schema.parse(settings) as DeploymentSettingsMap[T];
}
